import { BackendChapter, Chapter } from './Chapter';
import { ExerciseSeriesInfo } from './Exercise';
import {
  BackendStudentProgress,
  mapBackendStudentProgressToStudentProgress,
  StudentProgress,
} from './Progress';
import { BackendPage, mapBackendPageToPage, Page } from './Page';
import { AlternativeTitle, BackendAlternativeTitle, TrajectoryOwner } from './Differentiation';

export type TrajectoryViewMode = 'read' | 'edit';
export type TrajectorySectionCode = '0' | '1' | '2' | '3' | '4';
export const trajectorySectionCodeLookup: Record<TrajectorySectionCode, TrajectorySectionType> = {
  '0': 'opbouw',
  '1': 'theory',
  '2': 'exercises',
  '3': 'study_trainer',
  '4': 'study_guide',
};

export const trajectorySectionLabelLookup: Record<TrajectorySectionCode, string> = {
  '0': 'Opbouw',
  '1': 'Theorie',
  '2': 'Oefenen',
  '3': 'Studeren',
  '4': 'Opvolgen',
};

export const trajectorySectionorWorksheetTypeCodeLookup: Record<
  TrajectorySectionCode,
  TrajectorySectionType | WorksheetType
> = {
  '0': 'opbouw',
  '1': 'theory',
  '2': 'worksheet_exercises',
  '3': 'study_trainer',
  '4': 'study_guide',
};
export const trajectorySectionNameLookup: Record<TrajectorySectionType, TrajectorySectionCode> = {
  opbouw: '0',
  theory: '1',
  exercises: '2',
  study_trainer: '3',
  study_guide: '4',
};
export type WorksheetType = 'worksheet_exercises';
export type TrajectorySectionType =
  | 'opbouw'
  | 'theory'
  | 'exercises'
  | 'study_guide'
  | 'study_trainer';
export type TrajectorySectionOrWorksheetType = TrajectorySectionType | WorksheetType;
export type TrajectoryInfo = {
  id: string;
  title: string;
  number: string;
  parent: Trajectory | null;
  progress?: StudentProgress[];
  completedAt?: string;
  chapter?: Chapter;
  isForDefaultBook?: boolean;
  isDraft: boolean;
  isFrozen: boolean;
  isCollab: boolean;
  isOwner: boolean;
  isNotVisible: boolean;
  isPreferred?: boolean;
  isPreview: boolean;
  owner?: TrajectoryOwner;
  linkId?: string;
  linkTitle?: string;
  alternativeTitle?: string;
  alternativeTitles?: AlternativeTitle[];
  hasContent?: boolean;
};

export type Trajectory = TrajectoryInfo & {
  opbouw: string[] | false;
  theory: string[] | false;
  exercises: ExerciseSeriesInfo[] | false;
  worksheetExercises: Page[] | false;
};

export type ChapterWithTrajectories = Chapter & { trajectory: TrajectoryInfo[] };

export type TrajectoryWithChapterId = TrajectoryInfo & { chapterId: string };

export type PageType = 'opbouw' | 'theory' | 'study_guide';
export type OpbouwTheoryPageType = 'opbouw' | 'theory';
export type TrajectoryDetails = {
  pages: PageType[];
  exercises: {
    opbouw: number;
    theory: number;
    easy: number;
    average: number;
    hard: number;
  };
};

export type TrajectoryLockStatus = {
  locked: boolean;
  lockedBy?: LockedByType;
  timeLeft?: number;
};

export type LockedByType = {
  id: string;
  firstName: string;
  name: string;
};

// BACKEND

export type BackendTrajectoryInfo = Omit<
  TrajectoryInfo,
  | 'parent'
  | 'progress'
  | 'isDraft'
  | 'chapter'
  | 'completedAt'
  | 'isCollab'
  | 'isOwner'
  | 'isNotVisible'
  | 'owner'
  | 'isPreferred'
  | 'linkId'
  | 'isPreview'
  | 'alternativeTitles'
> & {
  parent: {
    data: BackendTrajectory | any;
  };
  progress?: {
    data: BackendStudentProgress[];
  };
  completed_at?: string;
  chapter?: BackendChapter;
  is_draft: boolean;
  is_frozen: boolean;
  is_collab: boolean;
  is_owner: boolean;
  is_not_visible: boolean;
  is_preferred?: boolean;
  is_preview: boolean;
  owner?: {
    data: TrajectoryOwner;
  };
  link_id?: string;
  link_title?: string;
  alternative_titles?: BackendAlternativeTitle[];
};

export type BackendTrajectory = Omit<
  Trajectory,
  | 'parent'
  | 'progress'
  | 'worksheetExercises'
  | 'isDraft'
  | 'isFrozen'
  | 'isCollab'
  | 'isOwner'
  | 'isNotVisible'
  | 'isPreferred'
  | 'owner'
  | 'linkId'
> & {
  parent: {
    data: BackendTrajectory | any;
  };
  progress?: {
    data: BackendStudentProgress[];
  };
  worksheet_exercises: BackendPage[] | false;
  is_draft: boolean;
  is_frozen: boolean;
  is_collab: boolean;
  is_owner: boolean;
  is_not_visible: boolean;
  is_preferred?: boolean;
  is_preview: boolean;
  owner?: {
    data: TrajectoryOwner;
  };
  link_id?: string;
  link_title?: string;
  alternative_title?: string;
};

// MAPPER

export const mapBackendTrajectoryToTrajectory = (data: BackendTrajectory): Trajectory => ({
  id: data.id,
  title: data.title,
  alternativeTitle: data.alternative_title,
  number: data.number,
  parent: data.parent
    ? data.parent.data.length !== 0
      ? mapBackendTrajectoryToTrajectory(data.parent.data)
      : null
    : null,
  progress: data.progress
    ? data.progress.data.map(mapBackendStudentProgressToStudentProgress)
    : undefined,
  isForDefaultBook: data.isForDefaultBook,
  opbouw: data.opbouw,
  theory: data.theory,
  exercises: data.exercises,
  worksheetExercises: !!data.worksheet_exercises
    ? data.worksheet_exercises.map(mapBackendPageToPage)
    : false,
  isDraft: data.is_draft,
  isFrozen: data.is_frozen,
  isCollab: data.is_collab,
  isOwner: data.is_owner,
  isNotVisible: data.is_not_visible,
  isPreferred: data.is_preferred,
  isPreview: data.is_preview,
  owner: data.owner?.data,
  linkId: data.link_id,
  linkTitle: data.link_title,
});

export const getTrajectoriesWithDetailsMapper = (
  response: any
): Record<string, TrajectoryDetails> => response.data;

export interface ITrajectoryDetail {
  id: string;
  title: string;
  parentId?: string;
  parentTitle?: string;
  parentCreatedAt?: Date;
  progress?: ITrajectoryDetailProgress[];
  isDraft: boolean;
  isFrozen: boolean;
  isCollab: boolean;
  isOwner: boolean;
  ownerName?: string;
  isPreview: boolean;
  number?: string;
  createdAt: Date;
  manual?: {
    userHasOpened: boolean;
    exists: boolean;
  }
  pages: {type: string, hasContent: boolean}[]
}

export interface ITrajectoryDetailProgress {
  type: string;
  isCompleted: boolean;
}

export interface IChapterTrajectory {
  id: string;
  title: string;
  number: string;
  isDraft: boolean;
  isVisible: boolean;
  isFrozen: boolean;
  isPreview: boolean;
}

export type ITrajectoryDifferentiation = {
  id: string;
  title: string;
  isPreferred: boolean;
  isOwner: boolean;
  isCollab: boolean;
  ownerName?: string;
  parentId: string;
  isDefaultVersion?: boolean;
  createdAt: Date;
  amountOfVisibleGroups: number;
};

export interface ICreatedTrajectory {
  id: string;
}

export type IGroupVisibility = {
  id: string;
  name: string;
  isSelected: boolean;
  isDisabled: boolean;
  isDefaultGroup: boolean;
  disabledReason?: string;
};
